import { type AnyAction } from 'redux';
import { locationActionTypes as actionTypes } from './actions';
import { LOCATION_VIEW_STATES } from '@/constants/locations';
import { type RootState } from '@/types/app';
import { type ILocationsState } from '@/types/locations';
import { getISODateInUTC } from '@/utils/dates';

export const initialState: ILocationsState = {
  activeLocationId: null,
  activeLocationChosenByDiner: false,
  error: false,
  list: [],
  detail: undefined,
  loading: false,
  meta: {
    lastUpdatedAt: null,
    pagination: null,
    retrieved_at: null,
  },
  params: null,
  searchLoading: false,
  moreLocationsLoading: false,
  searchTimestamp: null,
  viewState: LOCATION_VIEW_STATES.SEARCH,
  viewAllFailed: false,
  noDeliveryLocationsFound: false,
  invalidDeliverySearchAddress: false,
  googleAddressNotSelected: false,
  unresolvedAddress: false,
};

export const locations = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case actionTypes.FETCH_LOCATIONS:
    case actionTypes.FETCH_LOCATION:
    case actionTypes.HYDRATE_LOCATION:
      return Object.assign({}, state, {
        error: false,
        loading: true,
        noDeliveryLocationsFound: false,
        meta: {
          ...state.meta,
          lastUpdatedAt: getISODateInUTC(),
        },
      });
    case actionTypes.FETCH_ALL_LOCATIONS:
      return Object.assign({}, state, {
        error: false,
        loading: true,
        noDeliveryLocationsFound: false,
        // Set viewState immediately so a failure still reads VIEW_ALL, and
        // clear viewAllFailed so a retry starts clean.
        viewState: LOCATION_VIEW_STATES.VIEW_ALL,
        viewAllFailed: false,
        meta: {
          ...state.meta,
          lastUpdatedAt: getISODateInUTC(),
        },
      });
    case actionTypes.SEARCH_LOCATIONS:
    case actionTypes.SEARCH_DELIVERY_COVERAGE:
      return Object.assign({}, state, {
        error: false,
        searchLoading: true,
        unresolvedAddress: false,
      });
    case actionTypes.SEARCH_DELIVERY_COVERAGE_NO_RESULTS:
      return Object.assign({}, state, {
        error: true,
        searchLoading: false,
        noDeliveryLocationsFound: true,
      });
    case actionTypes.SHOW_DELIVERY_ADDRESS_INVALID_ERROR:
      return Object.assign({}, state, {
        error: true,
        searchLoading: false,
        invalidDeliverySearchAddress: true,
      });
    case actionTypes.SHOW_GOOGLE_ADDRESS_NOT_SELECTED_ERROR:
      return Object.assign({}, state, {
        error: true,
        searchLoading: false,
        invalidDeliverySearchAddress: false,
        googleAddressNotSelected: true,
      });
    case actionTypes.SEARCH_DELIVERY_COVERAGE_SUCCESS:
    case actionTypes.SEARCH_DELIVERY_COVERAGE_FAILURE:
      return Object.assign({}, state, {
        error: false,
        searchLoading: false,
        noDeliveryLocationsFound: false,
      });
    case actionTypes.FETCH_MORE_LOCATIONS:
      return Object.assign({}, state, {
        error: false,
        moreLocationsLoading: true,
      });
    case actionTypes.LOCATIONS_SUCCESS:
      return Object.assign({}, state, {
        error: false,
        list: action.list,
        loading: false,
        meta: action.meta,
        searchTimestamp: new Date(),
        viewState: LOCATION_VIEW_STATES.SEARCH,
      });
    case actionTypes.ALL_LOCATIONS_SUCCESS:
      return Object.assign({}, state, {
        error: false,
        list: action.list,
        loading: false,
        meta: action.meta,
        searchTimestamp: new Date(),
        viewState: LOCATION_VIEW_STATES.VIEW_ALL,
      });
    case actionTypes.MORE_LOCATIONS_SUCCESS:
      return Object.assign({}, state, {
        error: false,
        list: [...state.list, ...action.newList],
        moreLocationsLoading: false,
        meta: action.meta,
        searchTimestamp: new Date(),
        viewState: LOCATION_VIEW_STATES.SEARCH,
      });
    case actionTypes.SEARCH_LOCATIONS_SUCCESS:
      return Object.assign({}, state, {
        error: false,
        list: action.list,
        searchLoading: false,
        meta: action.meta,
        params: action.params,
        searchTimestamp: new Date(),
        viewState: LOCATION_VIEW_STATES.SEARCH,
        unresolvedAddress: false,
      });
    case actionTypes.SEARCH_LOCATIONS_UNRESOLVED:
      // A geocoder miss on the pickup search is recoverable, not a dead end: keep the
      // diner's search params (so their typed text stays put) and flag an empty state
      // the finder renders with a message + "view all locations" route (TEAM-14316).
      return Object.assign({}, state, {
        list: [],
        searchLoading: false,
        params: action.params,
        unresolvedAddress: true,
        searchTimestamp: new Date(),
        viewState: LOCATION_VIEW_STATES.SEARCH,
      });
    case actionTypes.LOCATION_SUCCESS:
      return Object.assign({}, state, {
        error: false,
        detail: action.detail,
        loading: false,
      });
    case actionTypes.LOCATIONS_FAIL:
      return Object.assign({}, state, {
        error: true,
        loading: false,
        searchLoading: false,
      });
    case actionTypes.VIEW_ALL_LOCATIONS_FAIL:
      return Object.assign({}, state, {
        error: true,
        loading: false,
        searchLoading: false,
        viewAllFailed: true,
      });

    case actionTypes.SET_ACTIVE_LOCATION:
      return Object.assign({}, state, {
        activeLocationId: action.id,
        activeLocationChosenByDiner: true,
      });

    // The default a fetch applies; unlike the diner's own pick, the next fetch may replace it
    // (TEAM-13911).
    case actionTypes.SET_DEFAULT_ACTIVE_LOCATION:
      return Object.assign({}, state, {
        activeLocationId: action.id,
        activeLocationChosenByDiner: false,
      });

    case actionTypes.CLEAR_DELIVERY_SEARCH_ERRORS:
      return Object.assign({}, state, {
        noDeliveryLocationsFound: false,
        invalidDeliverySearchAddress: false,
        googleAddressNotSelected: false,
      });
    case actionTypes.CLEAR_LOCATIONS:
      return Object.assign({}, initialState, {
        detail: state.detail,
      });
    default:
      return state;
  }
};

export const locationsSliceSelector = (state: RootState) => state.app.locations;
